<template>
  <Tooltip :content="displayTooltipContent" :enabled="!isMobileViewport">
    <div class="play-voice" :class="[`play-voice--${type}`]">
      <div
        class="play-btn"
        :class="[
          `play-btn--${type}`,
          {
            'play-btn--pending': loadingStatus,
            'play-btn--playing': isPlay,
          },
        ]"
        @click="handleClick"
      >
        <!-- 播放中状态 -->
        <div
          v-if="isPlay"
          class="play-btn__playing-wrap"
          :class="`play-btn__playing-wrap--${type}`"
        >
          <div class="play-btn__progress-bg" />
          <div class="play-btn__progress-circle" :style="progressStyle" />
          <Img
            :src="props.type === 'base' ? MusicPlay : MusicPlayPro"
            alt="playing"
            :object-fit="'contain'"
            class="play-btn__icon"
            :enable-oss="false"
          />
        </div>

        <!-- 加载中状态 -->
        <PlayPending v-else-if="loadingStatus" class="play-btn__icon-pending" />

        <!-- 默认停止状态 -->
        <PlayStop v-else class="play-btn__icon-stop" />
      </div>
      <span class="play-voice__desc">{{ displayDesc }}</span>
    </div>
  </Tooltip>
</template>
<script setup lang="ts">
import MusicPlayPro from '@rendora/design/assets/color/music-play-pro.png?url';
import MusicPlay from '@rendora/design/assets/color/music-play.png?url';
import PlayStop from '@rendora/design/assets/play-stop.svg?component';
import PlayPending from '@rendora/design/assets/spinner.svg?component';
import { Img } from '@rendora/design/components/img/index';
import Tooltip from '@rendora/design/components/tooltip/Tooltip.vue';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAudioStore } from '~/shared/store/audio';

const { t: $t } = useI18n();
const isMobileViewport = ref(false);
let mobileMediaQuery: MediaQueryList | null = null;

const updateMobileState = () => {
  if (!import.meta.client) return;
  isMobileViewport.value = window.matchMedia('(max-width: 767px)').matches;
};

const props = withDefaults(
  defineProps<{
    id: string;
    url: string;
    type: 'base' | 'pro';
  }>(),
  {
    type: 'base',
  },
);

const { togglePlay, stopAudio } = useAudioStore();
const { currentAudioId, isPlaying, isLoading, currentTime, duration } =
  storeToRefs(useAudioStore());

const isPlay = computed(() => isPlaying.value && currentAudioId.value === props.id);
const loadingStatus = computed(() => isLoading.value && currentAudioId.value === props.id);
const percent = computed(() => (currentTime.value * 100 * 3.6) / (duration.value || 1));
const displayDesc = computed(() => (props.type === 'base' ? $t('ji-chu') : 'Pro'));

const displayTooltipContent = computed(() => {
  if (props.type === 'base') {
    return $t('mian-fei-ji-chu-yin-se-fa-yin-liu-chang-qing-xi');
  }
  return $t('da-mo-xing-qu-dong-pi-mei-ren-sheng-3d-sheng-cheng-dao-chu-an-shi-chang-shou-fei');
});

// 根据类型设置进度圈样式
const progressStyle = computed(() => {
  const deg = percent.value;
  if (props.type === 'pro') {
    return `background: conic-gradient(var(--x-warning-10) ${deg}deg, var(--x-warning-4) ${deg}deg 360deg)`;
  }
  return `background: conic-gradient(var(--x-purple-6) ${deg}deg, var(--x-purple-2) ${deg}deg 360deg)`;
});

const handleClick = () => {
  togglePlay(props.id, props.url);
};

onMounted(() => {
  if (!import.meta.client) return;
  mobileMediaQuery = window.matchMedia('(max-width: 767px)');
  updateMobileState();
  mobileMediaQuery.addEventListener('change', updateMobileState);
});

onUnmounted(() => {
  mobileMediaQuery?.removeEventListener('change', updateMobileState);
  mobileMediaQuery = null;
  stopAudio();
});
</script>
<style lang="scss" scoped>
.play-voice {
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: center;
  font-size: var(--x-text-12-size);
  line-height: var(--x-text-12-line-height);

  &--base {
    color: var(--x-text-3);
  }

  &--pro {
    color: var(--x-warning-10);
  }

  .play-voice__desc {
    // color: var(--x-text-5);
  }
}

/* ==================== 播放按钮基础样式 ==================== */
.play-btn {
  position: relative;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
}

/* ==================== Base 类型样式 ==================== */
.play-btn--base {
  background: var(--x-back-3);
  color: var(--x-text-5);

  &:hover {
    background: var(--x-purple-1);
    color: var(--x-purple-6);
  }

  &.play-btn--playing {
    background: var(--x-purple-1);
    color: var(--x-purple-6);
  }

  &.play-btn--pending {
    animation: rotate 2s linear infinite;
    background: var(--x-purple-1);
    color: var(--x-purple-6);
  }
}

/* ==================== Pro 类型样式 ==================== */
.play-btn--pro {
  background: var(--x-warning-3);
  color: var(--x-warning-10);

  &:hover {
    background: var(--x-warning-4);
  }

  &.play-btn--playing {
    background: var(--x-warning-4);
    color: var(--x-warning-10);
  }

  &.play-btn--pending {
    animation: rotate 2s linear infinite;
    background: var(--x-warning-4);
    color: var(--x-warning-10);
  }
}

/* ==================== 播放中内部元素 ==================== */
.play-btn__playing-wrap {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.play-btn__playing-wrap--base {
  background: var(--x-purple-1);
}

.play-btn__playing-wrap--pro {
  background: var(--x-warning-3);
}

.play-btn__progress-bg {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 2;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
}

.play-btn__playing-wrap--base .play-btn__progress-bg {
  background: var(--x-purple-1);
}

.play-btn__playing-wrap--pro .play-btn__progress-bg {
  background: var(--x-warning-3);
}

.play-btn__progress-circle {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
}

.play-btn__icon {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 99;
  width: 14px;
  height: 14px;
  transform: translate(-50%, -50%);
}

/* ==================== 图标样式 ==================== */
.play-btn__icon-pending {
  width: 16px;
  height: 16px;
}

.play-btn__icon-stop {
  width: 16px;
  height: 16px;
}

/* ==================== 动画 ==================== */
@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
